import { getCookieByName } from '../utils/functions';
import useCurrentCategory from './useCurrentCategory';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export function useLargeIcons(
  enableLargeIcons: boolean,
  id: string,
  attribute?: string
) {
  const largeIconsCookie = getCookieByName('enableLargeIcons', true);
  const currentCategory = useCurrentCategory();
  const configContent = useSelector((state: any) => state.config?.content);

  const isAddonsStep = useSelector(
    (state: any) => state.workflow?.currentStep?.components
  )?.find((c) => c.attribute === 'addons');

  const isLargeIconsActive = useMemo(() => {
    if (![null, undefined].includes(largeIconsCookie)) {
      return largeIconsCookie as boolean;
    }
    if (![null, undefined, false].includes(enableLargeIcons)) {
      return enableLargeIcons as boolean;
    }
    return false;
  }, [largeIconsCookie, enableLargeIcons]);

  const fileNameImages = useMemo(() => {
    const allContents = Object.entries<{ [prop: string]: any }>(
      configContent
    ).reduce((acc, [_, props]) => {
      if (props && !isAddonsStep) {
        // check for duplicates
        if (Object.keys(props).find((prop) => acc[prop])) {
          return { ...acc };
        }
      }
      return { ...acc, ...props };
    }, {});

    //Clear case
    if (id.includes('clear'))
      return [
        'rxc-icon-treatments-clear.png',
        'rxc-icon-3d-treatments-clear.apng',
      ];

    //Proceed-as-is case
    if (id.includes('antiReflective$null')) {
      if (currentCategory === 'SUNGLASSES') {
        return [
          'rxc-icon-finishing-scratch_resistant_sun',
          'rxc-icon-3d-finishing-scratch_resistant_sun.apng',
        ];
      } else {
        return [
          'rxc-icon-finishing-proceed_as_is.png',
          'rxc-icon-3d-finishing-proceed_as_is.apng',
        ];
      }
    }
    let info = id.includes('sun')
      ? (configContent[attribute!]?.[id.split('$')?.[0]] ??
        allContents[id.split('$')[0]])
      : id.indexOf('$') >= 0
        ? (configContent[attribute!]?.[id.split('$')?.[1]] ??
          allContents[id.split('$')[1]])
        : (configContent[attribute!]?.[id] ?? allContents[id]);

    if (id.includes('sun') && !info) {
      info = {
        imageFileName1: 'rxc-icon-treatments-sun',
        imageFileName2: 'rxc-icon-3d-treatments-sun',
      };
    }
    let contentKeys = Object.keys(configContent);
    if (
      contentKeys.find((key: string) => key === 'treatmentFamily') &&
      configContent?.['treatmentFamily'] !== null
    ) {
      let tKey = Object.keys(configContent?.['treatmentFamily']);
      if (id.includes('transition') && tKey && allContents[tKey[0]]) {
        info = {
          imageFileName1: allContents[tKey[0]]?.imageFileName1,
          imageFileName2: allContents[tKey[0]]?.imageFileName2,
        };
      } else {
        if (id.includes('transition') && tKey) {
          info = {
            imageFileName1:
              configContent?.['treatmentFamily'][tKey[0]]?.imageFileName1,
            imageFileName2:
              configContent?.['treatmentFamily'][tKey[0]]?.imageFileName2,
          };
        } else if (id?.includes('blueLight') && tKey) {
          let correctTkey = tKey.filter((tk: any) =>
            tk?.toLowerCase()?.includes('blue')
          );
          let idKey = id?.split('$')?.[1];
          if (idKey && configContent?.['blueLight']?.[idKey]) {
            info = {
              imageFileName1:
                configContent?.['blueLight'][idKey]?.imageFileName1,
              imageFileName2:
                configContent?.['blueLight'][idKey]?.imageFileName2,
            };
          } else if (correctTkey?.length > 0) {
            info = {
              imageFileName1:
                configContent?.['treatmentFamily']?.[correctTkey[0]]
                  ?.imageFileName1,
              imageFileName2:
                configContent?.['treatmentFamily']?.[correctTkey[0]]
                  ?.imageFileName2,
            };
          } else {
            let value = id?.split('$')?.[1];
            info = {
              imageFileName1:
                configContent?.['blueLight']?.[value]?.imageFileName1,
              imageFileName2:
                configContent?.['blueLight']?.[value]?.imageFileName2,
            };
          }
        }
      }
    }
    if (!info) return null;
    const images = [];

    if (info.imageFileName1 && info.imageFileName2) {
      return [`${info.imageFileName1}.png`, `${info.imageFileName2}.apng`];
    } else if (info.imageFileName) {
      return [`${info.imageFileName}.apng`];
    }
    return images;
  }, [
    id,
    isLargeIconsActive,
    configContent,
    currentCategory,
    isAddonsStep,
    attribute,
  ]);

  return { fileNameImages, isLargeIconsActive };
}
